/**
 * What a device does with each thing the server tells it. Pure functions over one local record:
 * they return the record to store, `null` to delete it, or `undefined` to leave it alone — and
 * are run inside `mutateMap`'s transaction, against the record as it is at that instant.
 *
 * The rules, in one place:
 *  - a change made here that has not been uploaded (`dirty`) is never thrown away for anything
 *    older: the newer `updatedAt` wins, here exactly as on the server;
 *  - "was it uploaded?" is judged against the version that was SENT (`sentAt`): a map edited
 *    again while its upload was in flight stays dirty and goes up again;
 *  - the server's word on what it holds (image, thumbnail) is taken as it is.
 */
import type { MapPut, MapRecord, SyncAck } from '../../shared/api.ts';
import type { LocalMap } from '../store/idb.ts';

export function toPut(m: LocalMap): MapPut {
  return { id: m.id, kind: m.kind, title: m.title, createdAt: m.createdAt, updatedAt: m.updatedAt, deletedAt: m.deletedAt, imageType: m.imageType, imageBytes: m.imageBytes, w: m.doc.w, h: m.doc.h, doc: m.deletedAt ? null : m.doc };
}

/** The server's version, taken whole (keeping what only this device knows: which thumbnail it holds). */
export function fromRecord(rec: MapRecord, local?: LocalMap): LocalMap {
  return {
    id: rec.id,
    kind: rec.kind,
    title: rec.title,
    createdAt: rec.createdAt,
    updatedAt: rec.updatedAt,
    deletedAt: rec.deletedAt,
    rev: rec.rev,
    dirty: false,
    imageType: rec.imageType,
    imageBytes: rec.imageBytes,
    imageUploaded: rec.hasImage,
    // a thumbnail held here that the server lacks goes up; one made here and not yet sent still goes up
    thumbUploaded: local ? rec.hasThumb && local.thumbUploaded : true,
    thumbRev: rec.thumbRev,
    // (records from before thumbnails had revisions: what is held here is what the server has)
    thumbHave: local ? (local.thumbHave ?? (local.thumbUploaded ? rec.thumbRev : undefined)) : undefined,
    thumbStamp: local?.thumbStamp,
    doc: rec.doc ?? local?.doc ?? { v: 1, kind: rec.kind, w: rec.w, h: rec.h, scale: 1, legend: false, soils: { date: '19700101', initials: 'XX', areaLabel: { text: null, pos: null } }, items: [] },
  };
}

/** A record that changed on the server (from a pull, or the server's newer version in an ack). */
export function applyRecord(local: LocalMap | undefined, rec: MapRecord): LocalMap | null | undefined {
  if (rec.deletedAt !== null) {
    if (!local) return undefined;
    // deleted elsewhere — unless it was changed here since, and that change is still to go up
    return local.dirty && local.updatedAt > rec.updatedAt ? undefined : null;
  }
  if (!local) return fromRecord(rec);
  if (local.dirty) return rec.updatedAt > local.updatedAt ? fromRecord(rec, local) : undefined;
  if (rec.updatedAt < local.updatedAt) return undefined; // (never older than what is here)
  const same = rec.rev === local.rev && rec.updatedAt === local.updatedAt && rec.hasImage === local.imageUploaded && rec.thumbRev === (local.thumbRev ?? -1);
  return same ? undefined : fromRecord(rec, local);
}

/** The server's answer to one upload. `sentAt` is the `updatedAt` of the version that was sent. */
export function applyAck(local: LocalMap | undefined, ack: SyncAck, sentAt: number): LocalMap | null | undefined {
  if (!local) return undefined;
  const unchanged = local.updatedAt === sentAt;
  if (ack.status === 'rejected') return unchanged ? { ...local, blocked: ack.error } : undefined;
  if (ack.status === 'newer') return applyRecord(local, ack.record);
  const rec = ack.record;
  if (unchanged && local.deletedAt !== null) return null; // the deletion is on the server: nothing to keep
  const { blocked: _blocked, ...rest } = local;
  // (the server's `updatedAt` is ours, unless this device's clock ran so far ahead that it was held back)
  return { ...rest, updatedAt: unchanged ? rec.updatedAt : local.updatedAt, rev: rec.rev, dirty: !unchanged, imageUploaded: rec.hasImage, thumbUploaded: rec.hasThumb && local.thumbUploaded, thumbRev: rec.thumbRev };
}

/**
 * The daily check: the index of every live map the server holds, against what is here. Returns
 * the ids to fetch (missing or out of date here) and the ids to send again (clean here, unknown
 * to the server). The second keeps data: a map the server does not have is uploaded, never
 * deleted — better a map deleted long ago comes back once than the only copy of one is lost.
 */
export function checkIndex(locals: LocalMap[], index: [string, number, number][]): { fetch: string[]; resend: string[] } {
  const server = new Map(index.map(([id, rev, thumbRev]) => [id, { rev, thumbRev }]));
  const here = new Map(locals.map((m) => [m.id, m]));
  const fetch: string[] = [];
  const resend: string[] = [];
  for (const [id, s] of server) {
    const m = here.get(id);
    if (!m) fetch.push(id);
    else if (!m.dirty && m.deletedAt === null && (m.rev !== s.rev || (m.thumbRev ?? -1) !== s.thumbRev)) fetch.push(id);
  }
  for (const m of locals) if (!m.dirty && m.deletedAt === null && m.rev > 0 && !server.has(m.id)) resend.push(m.id);
  return { fetch, resend };
}

/** What the library shows of a map: two lists with the same signatures need no new render. */
export function mapSignature(m: LocalMap): string {
  return `${m.id}|${m.updatedAt}|${m.rev}|${m.title}|${m.dirty ? 1 : 0}|${m.imageUploaded ? 1 : 0}|${m.thumbStamp ?? 0}`;
}
/** `next` with every map that has not changed replaced by the very object from `prev`; `prev` itself when nothing has. */
export function stableList(prev: LocalMap[], next: LocalMap[]): LocalMap[] {
  const old = new Map(prev.map((m) => [m.id, m]));
  let same = prev.length === next.length;
  const out = next.map((m, i) => {
    const o = old.get(m.id);
    const keep = o && mapSignature(o) === mapSignature(m) ? o : m;
    if (keep !== prev[i]) same = false;
    return keep;
  });
  return same ? prev : out;
}
